import { makeStyles } from '@material-ui/styles'
import CreateButton from 'pf9-ui-components/built/components/buttons/CreateButton'
import Button from 'pf9-ui-components/built/elements/button/Button'
import ToggleSwitch from 'pf9-ui-components/built/elements/ToggleSwitch'
import React, { useEffect, useMemo, useState } from 'react'
import useReactRouter from 'use-react-router'
import ApiClient from 'api-client/ApiClient'
import DocumentMeta from 'core/components/DocumentMeta'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'
import { isNilOrEmpty } from 'utils/fp'
import AlertDetailsDialog from '../alerts/AlertDetailsDialog'
import { ALERTS } from '../home/HomePage'
import AlertsCard from './AlertsCard'
import BusinessContinuityCard from './BusinessContinuityCard'
import ResourceAllocationAndCapacityCard from './ResourceCapacityAndUtilizationCard'
import VmwareConfigCard from './VmwareConfigCard'

const getResourceName = (key: string) => {
  switch (key) {
    case 'cpu':
      return 'vCPU'
    case 'memory':
      return 'RAM'
    case 'storage':
      return 'Storage'
    case 'floating_ips':
      return 'Floating IPs'
    case 'load_balancers':
      return 'Load Balancers'
    default:
      return key
  }
}

const computeUsagePercentage = (summary: { name: string } & Resource) => {
  if (summary.utilization == null) {
    if (summary.name === 'floating_ips') {
      return (summary.allocated / summary.capacity) * 100
    }
    return 0
  }
  return summary.utilization * 100
}

type Resource = {
  unit: string
  capacity: number
  allocated: number
  utilization: number | null
}

type ResourceUtilizationData = Resource & {
  name: string
  utilizationPercentage: number | null
}

type RegionSummary = {
  name: string
  cpu: Resource
  memory: Resource
  storage: Resource
  floating_ips: Resource
  load_balancers: Resource
}

const RESOURCE_KEYS = ['cpu', 'memory', 'storage', 'floating_ips', 'load_balancers']

const { glu } = ApiClient.getInstance()

export default function AdminConsole() {
  const classes = useStyles()
  const { match } = useReactRouter()
  const {
    params: { region: regionName },
  } = match
  const [regionSummary, setRegionSummary] = useState<RegionSummary | null>(null)

  useEffect(() => {
    const getRegionSummary = async () => {
      const summary = await glu.getRegionSummary(regionName)
      setRegionSummary(summary)
    }
    getRegionSummary()
  }, [regionName])

  const [selfServiceEnabled, setSelfServiceEnabled] = useState(false)

  const [selectedAlert, setSelectedAlert] = useState(null)
  const [showAlertDetailsDialog, setShowAlertDetailsDialog] = useState(false)

  const resourceUtilizationData: Array<ResourceUtilizationData> = useMemo(() => {
    if (isNilOrEmpty(regionSummary)) return []

    return RESOURCE_KEYS.map((key) => {
      const summary = regionSummary[key]
      return {
        name: getResourceName(key),
        allocated: summary.allocated,
        capacity: summary.capacity,
        utilization: summary.utilization,
        utilizationPercentage: computeUsagePercentage({ ...summary, name: key }),
        unit: summary.unit,
      }
    })
  }, [regionSummary])

  const handleAlertClick = (alert) => {
    setSelectedAlert(alert)
    setShowAlertDetailsDialog(true)
  }

  const handleAlertDetailsDialogClose = () => {
    setShowAlertDetailsDialog(false)
    setSelectedAlert(null)
  }

  return (
    <div className={classes.adminConsolePage}>
      <DocumentMeta title="Admin Console" breadcrumbs />
      {showAlertDetailsDialog && (
        <AlertDetailsDialog alert={selectedAlert} onClose={handleAlertDetailsDialogClose} />
      )}
      <div className={classes.adminConsolePageHeader}>
        <div>
          <Text variant="h3" className={classes.regionName}>
            {regionName}
          </Text>
          <Text variant="body2">vCenter Endpoint</Text>
        </div>
        <div className={classes.actionsContainer}>
          <ToggleSwitch
            active={selfServiceEnabled}
            onClick={setSelfServiceEnabled}
            label="Enable Self Service"
            className={classes.toggleSwitch}
          />
          <Button variant="primary" icon="arrow-up-right-from-square">
            Convert to KVM
          </Button>
          <CreateButton className={classes.button}>Deploy New Resources</CreateButton>
        </div>
      </div>
      <div className={classes.headerCardsRow}>
        <VmwareConfigCard className={classes.card} />
        <BusinessContinuityCard className={classes.card} />
      </div>
      <div className={classes.capacityAndAlertsRow}>
        <ResourceAllocationAndCapacityCard resources={resourceUtilizationData} />
        <AlertsCard
          region={regionName}
          onAlertClick={handleAlertClick}
          alerts={ALERTS[regionName]}
        />
      </div>
    </div>
  )
}

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  adminConsolePage: {
    maxWidth: '1600px',
    display: 'grid',
    gridGap: theme.spacing(3),
  },
  adminConsolePageHeader: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
  },
  regionName: {
    fontWeight: 400,
    fontSize: '32px',
    marginBottom: theme.spacing(1),
  },
  actionsContainer: {
    display: 'grid',
    gridAutoFlow: 'column',
    gap: theme.spacing(3),
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  toggleSwitch: {
    alignSelf: 'center',
  },
  headerCardsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  card: {
    flexGrow: 1,
    minWidth: 'max-content',
  },
  capacityAndAlertsRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(600px, 700px) minmax(360px, 1fr)',
    gap: theme.spacing(2),
  },
}))
